import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import WorldMap from "../components/WorldMap";
import CountdownTimer from "../components/CountdownTimer";
import DocumentScoreCard from "../components/DocumentScoreCard";
import {
  FolderKanban,
  Clock,
  CheckCircle,
  Upload,
  FileText,
  AlertCircle,
  ArrowRight,
  FileCheck,
  Rocket
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeveloperDashboard() {
  const [projects, setProjects] = useState([]);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [documentModal, setDocumentModal] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const [projectsRes, mapRes] = await Promise.all([
        axios.get(`${API}/developer/projects`),
        axios.get(`${API}/map-data`)
      ]);
      setProjects(projectsRes.data);
      setMapData(mapRes.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getScoreColor = (score) => {
    if (score >= 800) return "text-emerald-600";
    if (score >= 600) return "text-blue-600";
    if (score >= 400) return "text-amber-600";
    return "text-red-600";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case "in_progress":
        return <Clock className="w-5 h-5 text-amber-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Calculate totals
  const avgScore = projects.length > 0 
    ? Math.round(projects.reduce((sum, p) => sum + (p.score_data?.score || 0), 0) / projects.length)
    : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="developer-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Developer Dashboard</h1>
        <p className="text-slate-500 mt-1">Manage your tokenized projects and document submissions</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-my-projects">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">My Projects</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{projects.length}</p>
                <p className="text-sm text-[#3B82F6] mt-2 flex items-center gap-1">
                  <Rocket className="w-3 h-3" />
                  All launching soon
                </p>
              </div>
              <div className="w-12 h-12 bg-[#E8F4FD] rounded-xl flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-[#3B82F6]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-avg-score">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Average Score</p>
                <p className={`text-3xl font-bold mt-2 ${getScoreColor(avgScore)}`}>{avgScore}/1000</p>
                <p className="text-sm text-slate-500 mt-2">Document compliance</p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <FileCheck className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="stat-pending-docs">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Documents Pending</p>
                <p className="text-3xl font-bold text-amber-600 mt-2">
                  {projects.reduce((sum, p) => sum + (p.score_data?.pending_documents || 0), 0)}
                </p>
                <p className="text-sm text-amber-600 mt-2">Awaiting approval</p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* World Map */}
      {mapData && <WorldMap projects={mapData.projects} />}

      {/* Launch Countdowns */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">Your Launch Schedules</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {projects.map((project) => (
              <CountdownTimer
                key={project.id}
                launchDate={project.launch_date}
                projectName={project.name}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Projects List */}
      <Card className="border-slate-200" data-testid="developer-projects">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">My Projects</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {projects.map((project) => {
              const score = project.score_data?.score || 0;
              
              return (
                <div
                  key={project.id}
                  className="border border-slate-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors"
                  data-testid={`dev-project-${project.id}`}
                >
                  <div className="flex flex-col md:flex-row">
                    <img
                      src={project.image_url}
                      alt={project.name}
                      className="w-full md:w-48 h-32 object-cover"
                    />
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-900 text-lg">{project.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="industry-tag">{project.industry}</span>
                            <span className="bg-[#0F2744] text-white px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                              <Rocket className="w-3 h-3" />
                              Launching
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${getScoreColor(score)}`}>{score}/1000</p>
                          <p className="text-xs text-slate-500">Document Score</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-slate-500">Token Price</p>
                          <p className="font-semibold text-slate-900">{formatCurrency(project.token_price)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Token Supply</p>
                          <p className="font-semibold text-slate-900">{project.token_supply.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Launch Date</p>
                          <p className="font-semibold text-slate-900">
                            {new Date(project.launch_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Expected ROI</p>
                          <p className="font-semibold text-emerald-600">{project.roi_percentage}%</p>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="flex justify-between text-sm text-slate-500 mb-2">
                          <span>Document Completion</span>
                          <span>{project.score_data?.completion_percentage || 0}%</span>
                        </div>
                        <Progress
                          value={project.score_data?.completion_percentage || 0}
                          className="h-2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Document Summary */}
                  <div className="border-t border-slate-100 p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-700">Document Status</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedProject(project);
                          setDocumentModal(true);
                        }}
                        data-testid={`upload-docs-${project.id}`}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Documents
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center justify-center gap-1 text-emerald-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="font-bold text-xl">{project.score_data?.approved_documents || 0}</span>
                        </div>
                        <p className="text-xs text-emerald-600">Approved</p>
                      </div>
                      <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center justify-center gap-1 text-amber-600">
                          <Clock className="w-4 h-4" />
                          <span className="font-bold text-xl">{project.score_data?.pending_documents || 0}</span>
                        </div>
                        <p className="text-xs text-amber-600">Pending</p>
                      </div>
                      <div className="text-center p-3 bg-slate-100 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-center gap-1 text-slate-500">
                          <AlertCircle className="w-4 h-4" />
                          <span className="font-bold text-xl">{project.score_data?.missing_documents || 0}</span>
                        </div>
                        <p className="text-xs text-slate-500">Missing</p>
                      </div>
                    </div>
                  </div>

                  {/* Milestones */}
                  <div className="border-t border-slate-100 p-4">
                    <h4 className="font-medium text-slate-700 mb-3">Project Milestones</h4>
                    <div className="flex items-center gap-2 overflow-x-auto pb-2">
                      {project.milestones?.map((milestone, idx) => (
                        <div key={idx} className="flex items-center">
                          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                            milestone.status === "completed" ? "bg-emerald-50 border-emerald-200" :
                            milestone.status === "in_progress" ? "bg-amber-50 border-amber-200" :
                            "bg-white border-slate-200"
                          }`}>
                            {getStatusIcon(milestone.status)}
                            <div>
                              <p className="text-sm font-medium text-slate-700">{milestone.name}</p>
                              <p className="text-xs text-slate-500">{formatCurrency(milestone.target)}</p>
                            </div>
                          </div>
                          {idx < project.milestones.length - 1 && (
                            <ArrowRight className="w-4 h-4 text-slate-300 mx-2 flex-shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Document Upload Modal */}
      <Dialog open={documentModal} onOpenChange={setDocumentModal}>
        <DialogContent className="max-w-2xl" data-testid="document-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              Upload Documents - {selectedProject?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Upload required documents to improve your project score. Higher scores increase investor confidence.
            </p>
            
            {selectedProject && (
              <DocumentScoreCard 
                scoreData={selectedProject.score_data}
                showDetails={false}
              />
            )}

            <div className="mt-6 p-4 border-2 border-dashed border-slate-300 rounded-lg text-center">
              <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">Drag and drop files here</p>
              <p className="text-sm text-slate-500 mt-1">or click to browse</p>
              <Button variant="outline" className="mt-4">
                Choose Files
              </Button>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Document Categories:</strong> Core Tokenization, Legal & Compliance, 
                Financial Validation, Project Validation, Asset Verification, Technology & Blockchain, 
                Governance & Management, Investor Reporting, STO Platform, Marketing & Communication
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentModal(false)} className="text-slate-900">
              Cancel
            </Button>
            <Button className="bg-[#0F2744] hover:bg-[#1E3A5F] text-white">
              Upload Documents
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
